import React from "react";
import { FaStar } from "react-icons/fa";

interface Card {
  id: string;
  type: string;
  is_default?: boolean;
  is_declined?: boolean;
  card: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
    display_brand?: string;
  };
  billing_details?: any;
}

interface SelectCardDialogProps {
  open: boolean;
  onClose: () => void;
  cards: Card[];
  selectedCardId: string;
  onSelect: (id: string) => void;
  onEdit?: (id: string) => void;
  onAddNew: () => void;
  onPay: () => void;
  onDelete: (id: string) => void;
  onSetDefault: (id: string) => void;
}

const SelectCardDialog: React.FC<SelectCardDialogProps> = ({
  open,
  onClose,
  cards,
  selectedCardId,
  onSelect,
  onEdit,
  onAddNew,
  onDelete,
  onSetDefault,
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-8 relative">
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          onClick={onClose}
          aria-label="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-6 h-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <div className="text-lg font-semibold mb-6">Select your card</div>
        <div className="flex flex-col gap-4 mb-6">
          {cards.map((card) => (
            <div
              key={card.id}
              className={`flex items-center justify-between bg-white rounded-xl border transition-all px-4 py-3 cursor-pointer ${
                selectedCardId === card.id
                  ? "border-green-400 shadow-[0_0_0_2px_#34d399]"
                  : "border-gray-200"
              }`}
              onClick={() => onSelect(card.id)}
            >
              <div className="flex items-center gap-4">
                <span className="text-lg font-bold text-blue-900">
                  {card.card.display_brand || card.card.brand}
                </span>
                <span className="tracking-widest font-mono text-base">
                  **** **** **** {card.card.last4}
                </span>
                <span className="text-xs text-gray-400 ml-2">
                  {card.card.display_brand || card.card.brand} • Expires{" "}
                  {card.card.exp_month}/{card.card.exp_year}
                </span>
                {card.is_default && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                    Default
                  </span>
                )}
                {card.is_declined && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded flex items-center gap-1">
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Declined
                  </span>
                )}
                {!card.is_default && (
                  <button
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSetDefault(card.id);
                    }}
                    title="Set as default"
                  >
                    <FaStar className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {onEdit && (
                  <button
                    className="p-2 text-gray-400 hover:text-gray-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(card.id);
                    }}
                    aria-label="Edit card"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16.862 4.487a2.1 2.1 0 1 1 2.97 2.97L7.5 19.788 3 21l1.212-4.5 12.65-12.013z"
                      />
                    </svg>
                  </button>
                )}
                <button
                  className={`p-2 ${
                    card.is_default
                      ? "text-gray-200 cursor-not-allowed"
                      : "text-red-400 hover:text-red-600"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!card.is_default) onDelete(card.id);
                  }}
                  aria-label="Delete card"
                  disabled={card.is_default}
                  title={
                    card.is_default
                      ? "Cannot delete default card"
                      : "Delete card"
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-5 h-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
          <div
            className="flex flex-col items-center justify-center bg-gray-100 rounded-xl border-2 border-dashed border-gray-200 py-8 cursor-pointer hover:bg-gray-200 transition"
            onClick={onAddNew}
          >
            <div className="flex items-center gap-2 text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-6 h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4.5v15m7.5-7.5h-15"
                />
              </svg>
              <span className="font-medium">Add new card</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectCardDialog;
